/**
 * Town liberation state helpers (HEX-122 / ADR-0018 §4).
 *
 * Towns are boss-held objectives until liberated. The spawn town at (1, 1) is
 * hard-wired as liberated from first load (ADR-0018 §4). All frontier towns
 * start as 'evil'; beating their boss marks them 'liberated'.
 *
 * State is stored in `TownLifecycleState.townState` (JSON blob in
 * `tile_metadata.lifecycle_state`) — additive, no DDL. Pre-v13 rows lack the
 * field; this module applies the correct read-time default.
 */

import type { Tile } from '../core/tile';
import type { TownLifecycleState, TownState } from '../core/tile';
import type { Persistence } from '../persistence/Persistence';

/** World coordinates of the spawn-town (origin cluster, HEX-36). */
export const SPAWN_TOWN_Q = 1;
export const SPAWN_TOWN_R = 1;

/** True when `(q, r)` is the origin spawn town. */
export function isSpawnTown(q: number, r: number): boolean {
  return q === SPAWN_TOWN_Q && r === SPAWN_TOWN_R;
}

/**
 * Read the town state from a town tile. Applies read-time defaults for
 * pre-v13 saves (no `townState` field):
 *   - origin spawn town `(1, 1)` → `'liberated'`
 *   - all other towns → `'evil'`
 */
export function getTownState(tile: Tile): TownState {
  if (tile.type !== 'town') {
    throw new Error(`getTownState called on non-town tile type ${tile.type}`);
  }
  const state = tile.lifecycleState as TownLifecycleState | null;
  if (state?.townState !== undefined && state.townState !== null) {
    return state.townState;
  }
  return isSpawnTown(tile.q, tile.r) ? 'liberated' : 'evil';
}

export interface LiberateTownDeps {
  readonly persistence: Pick<Persistence, 'saveTileMutations'>;
  readonly renderer?: { updateHex(hex: { q: number; r: number }, tile: Tile): void };
}

/**
 * Liberate a town in place: marks the town tile as 'liberated' and activates
 * the reserved pillar-slot tile so it materialises as a functional pillar.
 * Both tiles are mutated in memory and persisted.
 *
 * `pillarSlotTile` MUST be the `ground` tile at `townTile.lifecycleState.anchorTiles.pillar`.
 * If already a pillar (already liberated), this is a no-op.
 */
export async function liberateTown(
  townTile: Tile,
  pillarSlotTile: Tile,
  deps: LiberateTownDeps,
): Promise<void> {
  if (townTile.type !== 'town') return;

  const townState = townTile.lifecycleState as TownLifecycleState | null;
  if (townState === null) return;

  if (townState.townState === 'liberated') return;

  townTile.lifecycleState = { ...townState, townState: 'liberated' };
  await deps.persistence.saveTileMutations(townTile);
  deps.renderer?.updateHex({ q: townTile.q, r: townTile.r }, townTile);

  if (pillarSlotTile.type === 'ground') {
    pillarSlotTile.type = 'pillar';
    pillarSlotTile.lifecycleState = { kind: 'activatedPillarSlot' };
    await deps.persistence.saveTileMutations(pillarSlotTile);
    deps.renderer?.updateHex({ q: pillarSlotTile.q, r: pillarSlotTile.r }, pillarSlotTile);
  }
}
